import React, { useState, useMemo, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Confetti from 'react-confetti'
import { Card } from '../components/ui/card'
import { useUser } from '../hooks/useUser'
import classNames from 'classnames'
import { KeysForm } from './keys'
import { ProfilesTableNew } from '../components/profiles/ProfilesTableNew'
import { DefaultProfileSelect } from '../components/profiles/DefaultProfileSelect'
import { MerchjarLogoMark } from '../MerchjarLogo'
import { Button } from '../components/ui/button'
import { AuthPageChrome } from '../components/auth/auth_card'

const steps = [
    { name: 'Link with Amazon' },
    { name: 'Profile Nicknames' },
    { name: 'Default Profile' },
    { name: 'Managed Profiles' },
    { name: 'Finish' },
]

export const Onboard = ({}) => {
    const location = useLocation()
    const urlParams = new URLSearchParams(location.search)

    const [step, setStep] = useState(parseInt(urlParams.get('step') || 0))
    const { hasKeys } = useUser()

    const resolveHasSkip = (step) => {
        switch (step) {
            case 1:
                return false
            default:
                return true
        }
    }

    const [isNarrowStep, stepDescription] = useMemo(() => {
        switch (Number(step)) {
            default:
            case 0:
                return [true, '']
            case 1:
                return [
                    true,
                    'Connect your Amazon Ads account to sync campaigns, reporting, and bids.',
                ]
            case 2:
                return [
                    false,
                    'Profile nicknames allow you to set custom names for each of your ' +
                        'profiles. Nicknames are completely optional and give you an easy way to identify ' +
                        'which profile is which.',
                ]
            case 3:
                return [
                    true,
                    'The default profile will be the profile that will be selected when' +
                        ' you first load Merch Jar. Usually people set this to their account with the' +
                        ' highest ad spend.',
                ]
            case 4:
                return [
                    false,
                    'Merch Jar will pull in data for any profiles that you have selected' +
                        ' to be managed. You will want to have at least one profile selected!',
                ]
            case 5:
                return [
                    true,
                    'Setup complete! Merch Jar has already started pulling in your data.' +
                        " We'll send you an email in an hour or two when your first data pull is finished.",
                ]
        }
    }, [step])

    return (
        <>
            {step === 5 && <OnboardingConfetti />}
            <AuthPageChrome contentClassName="max-w-[1180px]">
                {step === 0 && (
                    <div className="group/card flex w-full max-w-[460px] flex-col items-center text-center">
                        <div className="relative z-10 mb-[-24px] translate-y-1 transition-transform duration-200 ease-out group-hover/card:-translate-y-0.5 group-focus-within/card:-translate-y-0.5">
                            <MerchjarLogoMark width={58} height={53} />
                        </div>
                        <Card className="w-full p-0 !rounded-[18px] !shadow-[0_22px_55px_rgba(14,41,71,0.16),0_8px_18px_rgba(14,41,71,0.08)]">
                            <div className="px-8 pb-8 pt-12">
                                <h1 className="text-2xl font-semibold text-gray-900">
                                    Welcome to Merch Jar
                                </h1>

                                <p className="mb-8 mt-4 text-base text-gray-500">
                                    Automate and scale your Amazon advertising with intelligent
                                    workflows built for modern brands.
                                </p>

                                <Button
                                    large
                                    className="px-8"
                                    text="Get started ->"
                                    intent="primary"
                                    onClick={() => setStep(1)}
                                />
                            </div>
                        </Card>
                    </div>
                )}

                {step > 0 && (
                    <Steps
                        current={step}
                        total={steps.length}
                        description={stepDescription}
                        setStep={(v) => setStep(v)}
                        hasBack={step > 1}
                        hasSkip={resolveHasSkip(step)}
                        allowNext={step !== 1 || hasKeys}
                        isNarrow={isNarrowStep}
                    >
                        {step === 1 && <KeysForm hideTitle />}
                        {step === 2 && <Nicknames />}
                        {step === 3 && <DefaultProfile />}
                        {step === 4 && <ManagedProfiles />}
                        {step === 5 && <Finish />}
                    </Steps>
                )}
            </AuthPageChrome>
        </>
    )
}

const Nicknames = ({}) => {
    return (
        <div>
            <ProfilesTableNew
                stickyFooter={false}
                showDefaultProfile={false}
                showApiAccess={false}
                showManaged={false}
                showRefresh
                emptyMessage={
                    <>
                        No profiles are showing yet. You can refresh to check Amazon again, or
                        continue onboarding and come back after the account sync finishes.
                    </>
                }
            />
        </div>
    )
}

const DefaultProfile = ({}) => {
    return (
        <div className="pt-2">
            <DefaultProfileSelect />
        </div>
    )
}

const ManagedProfiles = ({}) => {
    return (
        <div>
            <ProfilesTableNew
                showDefaultProfile={false}
                showApiAccess={false}
                stickyFooter={false}
            />
        </div>
    )
}

const Finish = ({}) => {
    return null
}

const OnboardingConfetti = () => {
    const [size, setSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    })

    useEffect(() => {
        const onResize = () =>
            setSize({
                width: window.innerWidth,
                height: window.innerHeight,
            })

        window.addEventListener('resize', onResize)
        return () => window.removeEventListener('resize', onResize)
    }, [])

    return (
        <Confetti
            width={size.width}
            height={size.height}
            recycle={false}
            numberOfPieces={260}
            gravity={0.22}
            tweenDuration={4500}
            colors={['#2d72d2', '#22A06B', '#F59E0B', '#DB2777', '#7C3AED', '#06B6D4']}
            style={{
                position: 'fixed',
                inset: 0,
                pointerEvents: 'none',
                zIndex: 60,
            }}
        />
    )
}

const Steps = ({
    current,
    total,
    children,
    description,
    allowNext = true,
    hasBack = true,
    hasSkip = false,
    setStep,
    isNarrow,
}) => {
    const navigate = useNavigate()
    const [isCardActive, setIsCardActive] = useState(false)

    if (current > steps.length) {
        return null
    }

    return (
        <div
            className={classNames('group/card flex w-full flex-col items-center', {
                'max-w-[620px]': isNarrow,
                'max-w-[1180px]': !isNarrow,
            })}
            onMouseEnter={() => setIsCardActive(true)}
            onMouseLeave={() => setIsCardActive(false)}
            onFocus={() => setIsCardActive(true)}
            onBlur={() => setIsCardActive(false)}
        >
            <div
                className={classNames(
                    'relative z-10 mb-[-24px] transition-transform duration-200 ease-out',
                    isCardActive ? '-translate-y-0.5' : 'translate-y-1',
                )}
            >
                <MerchjarLogoMark width={58} height={53} />
            </div>

            <Card className="w-full p-0 !rounded-[18px] !shadow-[0_22px_55px_rgba(14,41,71,0.16),0_8px_18px_rgba(14,41,71,0.08)]">
                <div className="border-b border-gray-100 px-6 pb-5 pt-12 md:px-8">
                    <p className="text-xs font-semibold uppercase text-green-600">
                        Step {current} of {total}
                    </p>
                    <h1 className="mt-1 text-2xl font-semibold text-gray-900">
                        {steps[current - 1].name}
                    </h1>
                    <div className="max-w-xl">
                        <StepBar current={current} total={total} className="mt-4" />
                    </div>
                    {description && (
                        <p className="mt-3 max-w-2xl text-sm leading-5 text-gray-500">
                            {description}
                        </p>
                    )}
                </div>

                {current !== steps.length && (
                    <div
                        className={classNames({
                            'px-6 py-6 md:px-8': isNarrow,
                            'min-h-[360px] px-4 py-4 md:px-6 md:py-6': !isNarrow,
                        })}
                    >
                        <div
                            className={classNames({
                                'mx-auto max-w-xl': isNarrow,
                            })}
                        >
                            {children}
                        </div>
                    </div>
                )}

                <div className="flex items-center justify-between rounded-b-[18px] border-t border-gray-100 bg-gray-50 px-4 py-4">
                    {hasBack && (
                        <Button
                            minimal
                            text="Back"
                            icon="arrow-left"
                            onClick={() => setStep((prev) => prev - 1)}
                        />
                    )}
                    {!hasBack && <span />}
                    <div className="flex items-center justify-end gap-3">
                        {hasSkip && current != steps.length && (
                            <Button
                                minimal
                                text="Skip"
                                onClick={() => setStep((prev) => prev + 1)}
                            />
                        )}
                        {current == steps.length ? (
                            <Button
                                large
                                className="px-8"
                                intent="primary"
                                onClick={() => navigate('/dashboard')}
                                text="Finish"
                            />
                        ) : (
                            <Button
                                large
                                className="px-8"
                                intent="primary"
                                disabled={!allowNext}
                                onClick={() => setStep((prev) => prev + 1)}
                                text="Next ->"
                            />
                        )}
                    </div>
                </div>
            </Card>
        </div>
    )
}

const StepBar = ({ current, total, className }) => {
    const steps = useMemo(() => {
        const out = []
        for (let i = 1; i <= total; i++) {
            out.push(
                <div
                    key={i}
                    className={classNames('border-b-2 w-full', {
                        'border-green-500': i <= current,
                    })}
                />,
            )
        }

        return out
    }, [current, total])

    return <div className={classNames('flex space-x-2', className)}>{steps}</div>
}
